import {
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    inject,
    input,
    InputSignal,
    output,
    OutputEmitterRef,
    signal,
    Signal,
    WritableSignal
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { IIroNamRequest, IroNamRequestStatus, IUser } from '@shs-digi-care-monorepo/interfaces';
import { select } from '@ngxs/store';
import { catchError, tap } from 'rxjs';
import { TransitionService } from '../../../shared/services/transition.service';
import { LoggingService, SnackbarService, UserState } from '@shs-digi-care-monorepo/frontend-lib';

@Component({
    selector: 'app-assign-to-me-button',
    templateUrl: './assign-to-me-button.component.html',
    imports: [TranslateModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AssignToMeButtonComponent {
    private transitionService = inject(TransitionService);
    private snackbar = inject(SnackbarService);
    private logger = inject(LoggingService);

    public request: InputSignal<IIroNamRequest> = input.required<IIroNamRequest>();
    public assigned: OutputEmitterRef<IUser> = output<IUser>();

    protected me: Signal<IUser | null> = select(UserState.me);
    protected loading: WritableSignal<boolean> = signal(false);
    protected readonly RequestState = IroNamRequestStatus;

    protected assignToMe(event: Event): void {
        event.stopPropagation();
        event.preventDefault();

        if (!this.me()) {
            this.logger.warn('Cannot assign: current user not loaded');
            this.snackbar.error('request.errors.assign-no-user');
            return;
        }

        const requestId = this.request().id;
        const gid = this.me()?.gid ?? '';
        this.logger.info('Assigning IRO request to me', { requestId, gid });
        this.loading.set(true);
        this.transitionService
            .assign(requestId, gid)
            .pipe(
                tap(() => {
                    this.logger.info('IRO request assigned successfully', { requestId, gid });
                    this.loading.set(false);
                    const me = this.me();
                    if (me) this.assigned.emit(me);
                }),
                catchError((error) => {
                    this.logger.error('Failed to assign IRO request', {
                        requestId,
                        gid,
                        error: error?.message ?? error
                    });
                    this.loading.set(false);
                    this.snackbar.error('request.requestDetail.assigned-error');
                    return [];
                })
            )
            .subscribe();
    }
}
